/**
 * Occupation utilities — backed by activity.predicted_total.
 *
 * Frontend consumers call the existing `/activity/search` with { has_forecast: true, userIds: [...] }
 * and then use mapActivitiesToOccupations() to convert the raw activities into occupation-like objects.
 */

function getWorkingDaysInMonth(date) {
  const d = new Date(date);
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  let wd = 0;
  for (let i = 1; i <= daysInMonth; i++) {
    const day = new Date(d.getFullYear(), d.getMonth(), i).getDay();
    if (day !== 0 && day !== 6) wd++;
  }
  return wd;
}

export function hoursToPercent(hours, date) {
  const wd = getWorkingDaysInMonth(date);
  return wd > 0 ? Math.round((hours / (wd * 8)) * 100) : 0;
}

export function percentToHours(percent, date) {
  const wd = getWorkingDaysInMonth(date);
  return (percent / 100) * wd * 8;
}

/**
 * Map raw activities (from /activity/search with has_forecast:true) to occupation-like objects.
 * This is the single place that converts activity shape → occupation shape.
 */
export function mapActivitiesToOccupations(activities) {
  return activities.map((a) => ({
    _id: a._id,
    user_id: a.userId,
    user_name: a.userName,
    user_avatar: a.userAvatar,
    user_tjm: a.userTjm,
    user_tjms: a.userTjms,
    project_id: a.projectId,
    project_name: a.projectName,
    month: a.date,
    percent: hoursToPercent(a.predicted_total, a.date),
    predicted_total: a.predicted_total,
    user_payment_type: a.userPaymentType,
    user_contract: a.userContract,
  }));
}

export function isSameMonth(date, month) {
  const d = new Date(date);
  const m = new Date(month);
  return d.getFullYear() === m.getFullYear() && d.getMonth() === m.getMonth();
}

/**
 * Calculate total monthly occupation % from occupation entries.
 */
export function calculateMonthlyOccupation(occupations, month) {
  return occupations.reduce((sum, o) => sum + (o.percent || 0), 0);
}

/**
 * Get per-project aggregated occupations for display in occupation bars.
 */
export function getAggregatedOccupationsForMonth(occupations, month) {
  if (occupations.length === 0) return [];

  const projectData = {};
  occupations.forEach((o) => {
    const key = o.project_id || o.project_name || "unknown";
    if (!projectData[key]) {
      projectData[key] = { project_id: o.project_id, project_name: o.project_name, percent: 0, soft: o.soft };
    }
    projectData[key].percent += o.percent || 0;
  });
  return Object.values(projectData);
}

/**
 * Filter occupations for a specific user + month.
 */
export function filterOccupationsForMonth(allOccupations, userId, month) {
  return allOccupations.filter((o) => {
    if (o.user_id !== userId) return false;
    return isSameMonth(o.month, month);
  });
}

/**
 * Get total monthly occupation % for a user in a specific month.
 */
export function getUserMonthlyOccupation(allOccupations, userId, month) {
  const userMonthOccs = filterOccupationsForMonth(allOccupations, userId, month);
  return calculateMonthlyOccupation(userMonthOccs, month);
}
